import React from "react";
import PropTypes from "prop-types";
import { Formik, FieldArray } from "formik";
import uniqueId from "lodash/uniqueId";
import * as Yup from "yup";
import Grid from "@mui/material/Grid";
import {
  getRequiredMessage,
  getInvalidMessage
} from "../../../../../../utils/getValidationMessage";
import { AutoSaveField as Field, Input, CustomSelect } from "../../../../../../components/Form";
import { ContinueButton } from "../../../../../../components/Buttons/ContinueButton";
import { ArrayAddButton } from "../../../Buttons/ArrayAddButton";
import { ButtonWrapper, RemoveButton, Label } from "./styled";
import { PERCENTAGE_REGEX } from "../../../../../../utils/validation";
import { FormContainer } from "../CompanyBusinessRelationships/styled";
import { ScrollToError } from "components/ScrollToError";

const posDetailsSchema = () =>
  Yup.object().shape({
    machineProvider: Yup.array().of(
      Yup.object().shape(
        {
          networkprovider: Yup.string()
            .nullable()
            .max(100, "Maximum 100 characters allowed")
            .when("listOfMerchantID", {
              is: listOfMerchantID => listOfMerchantID,
              then: Yup.string().required(getRequiredMessage("Network Provider")), // NOSONAR
              otherwise: Yup.string()
            }),
          listOfMerchantID: Yup.string()
            .nullable()
            .when("networkprovider", {
              is: networkprovider => networkprovider,
              then: Yup.string().required(getRequiredMessage("List of all Merchant ID’s")), // NOSONAR
              otherwise: Yup.string()
            })
        },
        ["networkprovider", "listOfMerchantID"]
      )
    ),
    assignedToRabbani: Yup.string().max(100, "Maximum 10 characters allowed"),
    isAllAssignedToRabbani: Yup.string().max(10, "Maximum 10 characters allowed"),
    ifNoReason: Yup.string().max(100, "Maximum 100 characters allowed"),
    chargebackPercentage: Yup.string()
      .nullable(true)
      .matches(PERCENTAGE_REGEX, getInvalidMessage("Chargeback Percentage"))
  });

export const PosDetailsComponent = ({ handleContinue, updateProspect, machineProvider }) => {
  const NETWORK_LIMIT = 5;

  return (
    <Formik
      initialValues={{
        machineProvider: machineProvider?.map(item => ({ ...item, id: uniqueId() })) || [""],
        assignedToRabbani: "",
        isAllAssignedToRabbani: "",
        ifNoReason: "",
        chargebackPercentage: 0
      }}
      onSubmit={handleContinue}
      validationSchema={posDetailsSchema}
      validateOnChange={false}
    >
      {({ values }) => {
        const isMachineProvider = values.machineProvider.length > 1;

        return (
          <FormContainer>
            <ScrollToError />
            <Grid container spacing={2}>
              <FieldArray name="machineProvider">
                {arrayHelpers => (
                  <>
                    {values.machineProvider.map((item, index) => (
                      <Grid item sm={12} xs={12} key={item.id}>
                        <Grid item sm={10} xs={12}>
                          <Field
                            path={`roProspect.organizationInfoRO.posDetails.machineProvider[${index}].networkprovider`}
                            name={`machineProvider[${index}].networkprovider`}
                            label="Network Provider"
                            placeholder="Network Provider"
                            component={Input}
                            InputProps={{
                              inputProps: { tabIndex: 0 }
                            }}
                            contextualHelpText={
                              <>Network provider can have multiple merchant IDs.</>
                            }
                          />
                        </Grid>
                        <Grid item sm={10} xs={12}>
                          <Field
                            path={`roProspect.organizationInfoRO.posDetails.machineProvider[${index}.listOfMerchantID]`}
                            name={`machineProvider[${index}].listOfMerchantID`}
                            label="List of all Merchant ID's"
                            placeholder="List of all Merchant ID's"
                            component={Input}
                            InputProps={{
                              inputProps: { tabIndex: 0 }
                            }}
                            contextualHelpText={
                              <>
                                Comma separated list of Merchant IDs associated with the network
                                provider.
                              </>
                            }
                          />
                        </Grid>
                        {isMachineProvider && (
                          <Grid item sm={12} xs={12}>
                            <RemoveButton
                              arrayHelpers={arrayHelpers}
                              dataArray={values.machineProvider}
                              itemIndex={index}
                              updateProspect={updateProspect}
                              prospectPath="roProspect.organizationInfoRO.posDetails.machineProvider"
                              title="Delete"
                            />
                          </Grid>
                        )}
                      </Grid>
                    ))}
                    {values.machineProvider.length < NETWORK_LIMIT && (
                      <Grid item sm={12} xs={12}>
                        <ArrayAddButton
                          title="Add another network provider"
                          limit={NETWORK_LIMIT}
                          requiredFields={["networkprovider", "listOfMerchantID"]}
                          addedItem={{ networkprovider: "", listOfMerchantID: "" }}
                          dataArray={values.machineProvider}
                          arrayHelpers={arrayHelpers}
                          noUniqueID={true}
                        />
                      </Grid>
                    )}
                  </>
                )}
              </FieldArray>

              <Grid item sm={12} xs={12}>
                <Label variant="body1">Merchant ID's to be assigned to RAKBANK</Label>
                <Field
                  path="roProspect.organizationInfoRO.posDetails.merchantIDAssignedRabbani"
                  name="assignedToRabbani"
                  placeholder="Merchant ID's to be assigned to RAKBANK"
                  component={Input}
                  InputProps={{
                    inputProps: { tabIndex: 0 }
                  }}
                />
              </Grid>
              <Grid item sm={12} xs={12}>
                <Label variant="body1">
                  Are all the MID's that customer holds being assigned to RAKBANK?
                </Label>
                <Field
                  path="roProspect.organizationInfoRO.posDetails.areAllMIDAssignedToRabbani"
                  name="isAllAssignedToRabbani"
                  component={CustomSelect}
                  datalistId="YesNoList"
                  InputProps={{
                    inputProps: { tabIndex: 0 }
                  }}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  path="roProspect.organizationInfoRO.posDetails.remarks"
                  name="ifNoReason"
                  label="If NO, Reason"
                  placeholder="If NO, Reason"
                  component={Input}
                  InputProps={{
                    inputProps: { tabIndex: 0 }
                  }}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  path="roProspect.organizationInfoRO.posDetails.chargebackPercent"
                  name="chargebackPercentage"
                  label="Chargeback Percentage"
                  placeholder="Chargeback Percentage"
                  component={Input}
                  InputProps={{
                    inputProps: { tabIndex: 0 }
                  }}
                />
              </Grid>
            </Grid>

            <ButtonWrapper>
              <ContinueButton type="submit" />
            </ButtonWrapper>
          </FormContainer>
        );
      }}
    </Formik>
  );
};

PosDetailsComponent.propTypes = {
  /**
   * Function to handle form submission and continue to next step
   */
  handleContinue: PropTypes.func.isRequired,

  /**
   * Function to update prospect data in Redux store
   */
  updateProspect: PropTypes.func.isRequired,

  /**
   * Array of machine provider details with network provider and merchant ID information
   */
  machineProvider: PropTypes.arrayOf(
    PropTypes.shape({
      networkprovider: PropTypes.string,
      listOfMerchantID: PropTypes.string
    })
  )
};
